/* ==========================================================================
   EasyCashier – Försäljningsanalys
   --------------------------------------------------------------------------
   Datamodell (verifierad mot kvittoexport):
     receipt.receiptItems[].receiptItemType : ARTICLE | TEXT | BONUS_CHECK | PROMOTION
     receipt.receiptItems[].articleType     : PRODUCT | SERVICE | GIFT_CERTIFICATE
     receipt.receiptItems[].vat             : 0 | 0.06 | 0.12 | 0.25  (andel, ej procent)
     receipt.receiptPayments[].moneyBack    : boolean – växelraden har negativt belopp

   Avstämning som håller på öret:
     summa betalningar = alla radbelopp + öresavrundning

   Beräkningsregler:
     • Marginal = bruttovinst / omsättning EXKL moms. Momsen är 0, 6, 12 eller 25 %,
       aldrig en fast faktor – därför används radens retailTotalExcludingVat.
     • Presentkort (GIFT_CERTIFICATE) är en skuld, inte försäljning. De bokförs
       med negativ vinst i kvittot och hålls därför utanför produkt- och
       marginalanalysen. De redovisas separat.
     • Bonuscheckar (BONUS_CHECK) är en rabatt, inte en artikel. Redovisas separat.
     • TEXT-rader saknar belopp och ignoreras.
     • Returer har negativt antal och negativt belopp. De räknas med i netto och
       redovisas dessutom separat.
   ========================================================================== */

export const CFG = {
  MAX_ROWS: 500,          // tak för tabellrader i vyn
  MIN_QTY_LOW_MARGIN: 10, // tröskel för "säljer bra men tjänar minst"
  MAX_FILE_MB: 200
};

/* En kampanjrabatt som inte går att fördela på artiklarna hör till kvittot.
   Den redovisas under en egen rubrik i stället för bland artiklarna som
   saknar artikelgrupp eller leverantör — de listorna använder kunden för att
   hitta artiklar att rätta i artikelregistret. */
export const UNALLOCATED_LABEL = 'Kampanjrabatt utan artikel';

export const WEEKDAYS = ['Måndag','Tisdag','Onsdag','Torsdag','Fredag','Lördag','Söndag'];
export const PAYMENT_LABELS = {
  CARD:'Kort', CASH:'Kontant', SWISH:'Swish', GIFT_CERTIFICATE:'Presentkort',
  INVOICE:'Faktura', ACCOUNT:'Konto', BONUS:'Bonus'
};

/* Alternativa betalsätt lägger varje butik upp och namnger själv i
   EasyCashier. Exporten har dem som CUSTOM1, CUSTOM2 …; verktyget visar dem
   med numret, så som de heter i EasyCashier, och aldrig med ett namn ur
   betalningsraden — det är butikens eget och kan vara vad som helst. */
const ALTERNATIVE = /^CUSTOM(\d+)$/;

/** Läsbart namn på ett betalsätt: "Kort", "Alternativt betalsätt 1". */
export function paymentLabel(type){
  if(PAYMENT_LABELS[type]) return PAYMENT_LABELS[type];
  const m = ALTERNATIVE.exec(type || '');
  return m ? `Alternativt betalsätt ${Number(m[1])}` : String(type || 'Okänt betalsätt');
}

/** Sant för ett alternativt betalsätt, som får en förklaring i gränssnittet. */
export const isAlternativePayment = type => ALTERNATIVE.test(type || '');

/* --------------------------------------------------------------------------
   Butiker

   Exporten anger butiken med nummer, och varje kvitto har butikens
   companyName, address1, zipCode och city. Ett eget fält för butikens namn
   finns inte: i en export med tretton butiker hade alla samma companyName,
   alltså företagets. Butiken får därför sitt namn bara när varje butik i
   filen har ett eget companyName; annars gatuadressen, som skiljer butikerna
   åt; annars numret. Numret står alltid med, så att butiken går att känna
   igen i Back office.
   -------------------------------------------------------------------------- */

/** Etikett per butiksnummer, Map(nummer → "Storgatan 5 (butik 2)"). */
export function storeLabels(receipts){
  const info = new Map();
  for(const r of receipts){
    if(r.store == null || info.has(r.store)) continue;
    info.set(r.store, {name: clean(r.storeName), address: clean(r.storeAddress)});
  }
  const unique = key => {
    const vals = [...info.values()].map(i => i[key]);
    return vals.every(Boolean) && new Set(vals).size === vals.length;
  };
  const by = unique('name') ? 'name' : unique('address') ? 'address' : null;
  const out = new Map();
  for(const [nr, i] of info) out.set(nr, by ? `${i[by]} (butik ${nr})` : `Butik ${nr}`);
  return out;
}
const clean = v => typeof v === 'string' ? v.replace(/\s+/g, ' ').trim() : '';

/* Hur kvittot lämnade kassan. EasyCashier skickar slängda kvitton till
   trash-can@easycashier.se, men exporten översätter det redan till TRASH_CAN.
   Adressen används bara som reserv om typfältet skulle saknas. */
export const TRASH_MAILBOX = 'trash-can@easycashier.se';

export const DELIVERY = {
  PRINTER:   {label:'Papper',     desc:'Kvittot levererades på papper'},
  EMAIL:     {label:'E-post',     desc:'Kvittot skickades till kundens e-post'},
  TRASH_CAN: {label:'Bortvalt',   desc:'Kunden valde bort kvittot'},
  UNKNOWN:   {label:'Okänt',      desc:'Leveranssätt saknas i exporten'}
};
export const DELIVERY_ORDER = ['PRINTER','EMAIL','TRASH_CAN','UNKNOWN'];

/** Avgör hur ett kvitto levererades. */
export function deliveryOf(r){
  const t = r.recipientTypeOriginal;
  if(t && DELIVERY[t]) return t;
  if(typeof r.email === 'string' && r.email.trim().toLowerCase() === TRASH_MAILBOX) return 'TRASH_CAN';
  return 'UNKNOWN';
}

/* --------------------------------------------------------------------------
   Leverantörer

   Exporten har leverantörens nummer per rad, supplierNumber. Har Back office
   också lagt till namnet, supplierName, visas det. Numret är fortfarande
   identiteten: det står med i etiketten, så två leverantörer med samma namn
   aldrig slås ihop, och urvalet sparas på numret.
   -------------------------------------------------------------------------- */

/** "Telia (10)" när namnet finns, annars "Leverantör 10". */
export function supplierLabel(nr, name){
  if(!nr) return 'Saknar leverantör';
  return name ? `${name} (${nr})` : 'Leverantör ' + nr;
}

/** Leverantörsnamn per nummer. Första namnet som förekommer gäller, så att
    alla rader för samma leverantör får samma etikett även om några saknar namn. */
export function supplierNames(receipts){
  const names = new Map();
  for(const r of receipts) for(const ln of r.lines){
    if(ln.supplier && ln.supplierName && !names.has(ln.supplier)) names.set(ln.supplier, ln.supplierName);
  }
  return names;
}
